import { useCallback, useState } from 'react';
import { CallRouteV1 } from '@frontend/api-call-route';
import { useTranslation } from '@frontend/i18n';
import { sentry } from '@frontend/tracking';
import { SkeletonLoaders, useAlert, useFormField } from '@frontend/design-system';
import { AudioPicker, AudioPickerProps } from '../../../audio-picker/audio-picker';
import { AudioItem } from '../../../audio-picker/types';
import { Step } from './atoms/step';
import { AddStepPanelProps } from './types';

export const playMessageAllowedOptions: AudioPickerProps['allowedOptions'] = {
  add: true,
  custom: true,
  standard: false,
  mailbox: false,
  none: false,
  default: false,
  callRecordingNotice: true,
};

export const PlayMessageStep = ({
  tenantId,
  initialState,
  onProceedClick,
}: AddStepPanelProps<CallRouteV1.Types.NodeType.NODE_TYPE_PLAY_MESSAGE>) => {
  const { t } = useTranslation('phone');
  const alerts = useAlert();

  const [selectedAudioFile, setSelectedAudioFile] = useState<AudioItem | undefined>(undefined);

  const audioFileProps = useFormField({
    required: true,
    type: 'dropdown',
    value: initialState?.callObject?.playMessageExpansion?.mediaItemId,
  });

  const handleProceedClick = useCallback(() => {
    if (!selectedAudioFile) {
      alerts.error('No audio file selected');
      sentry.error({
        error: 'No audio file selected in Play Message step',
        topic: 'phone-config',
        team: 'fe-phone-config',
        severityLevel: 'error',
        addContext: {
          name: 'Step data',
          context: {
            initialState,
          },
        },
      });
      return;
    }

    onProceedClick({
      type: CallRouteV1.Types.NodeType.NODE_TYPE_PLAY_MESSAGE,
      callObject: {
        primitiveId: selectedAudioFile.id,
        primitiveName: selectedAudioFile.name,
        instructionId: initialState?.callObject?.instructionId ?? '',
        instructionSetId: initialState?.callObject?.instructionSetId ?? '',
        playMessageExpansion: {
          mediaFileName: initialState?.callObject?.playMessageExpansion?.mediaFileName ?? '',
          mediaItemId: selectedAudioFile.id,
        },
      },
    });
  }, [selectedAudioFile, alerts, onProceedClick, initialState]);

  return (
    <Step
      title={t('Play Message')}
      primaryButtonDisabled={!audioFileProps.value}
      onPrimaryButtonClick={handleProceedClick}
    >
      <SkeletonLoaders.Loader isLoading={false} shape='rectangle' width={320} height={50}>
        <AudioPicker
          widths={{ field: '300px', scrubber: '240px' }}
          allowedOptions={playMessageAllowedOptions}
          field={audioFileProps}
          name='mediaId'
          labels={{
            field: t('Select Audio File'),
          }}
          onSelect={(item) => {
            // NOTE: this will not be called if the user selects the "No Greeting option"
            setSelectedAudioFile(item);
          }}
          tenantId={tenantId}
        />
      </SkeletonLoaders.Loader>
    </Step>
  );
};
